import {
  type BrandKit,
  type BrandTokens,
  deltaEHex,
  nextTokenKey,
  type PaletteColor,
} from "@cva/shared";
import { Check, ImageIcon, Plus, Sparkles, Upload } from "lucide-react";
import { useRef, useState } from "react";
import { toast } from "sonner";
import { paletteFromBlob, paletteFromUrl } from "../lib/palette";
import { errorMessage } from "../lib/utils";
import { MediaPickerDialog } from "./media-picker";
import { Button } from "./ui/button";
import { Card, CardHeader } from "./ui/card";

/**
 * "Farben aus Bild übernehmen" on the brand page: dominant colours of an
 * uploaded file (analysed locally, not uploaded), a media library image or
 * the logo; each becomes a new brand colour with one click.
 */

/** Colours closer than this (ΔE2000) to a kit colour count as already there. */
const SAME_COLOR = 3;

export function BrandImageColors({
  orgId,
  kit,
  tokens,
  update,
}: {
  orgId: string;
  kit: BrandKit;
  tokens: BrandTokens;
  update: (recipe: (draft: BrandTokens) => void) => void;
}) {
  const [palette, setPalette] = useState<PaletteColor[] | null>(null);
  const [source, setSource] = useState("");
  const [loading, setLoading] = useState(false);
  const [picking, setPicking] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);
  const logo = kit.logos.find((entry) => entry.variant === "primary") ?? kit.logos.find(() => true);

  const run = async (label: string, load: () => Promise<PaletteColor[]>) => {
    setLoading(true);
    try {
      const colors = await load();
      setPalette(colors);
      setSource(label);
      if (colors.length === 0) toast.error("In diesem Bild wurden keine Farben gefunden.");
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const inKit = (color: string) =>
    tokens.colors.some((entry) => deltaEHex(entry.color, color) < SAME_COLOR);
  const add = (colors: string[]) => {
    const fresh = colors.filter((color) => !inKit(color));
    if (fresh.length === 0) return;
    update((draft) => {
      for (const color of fresh) {
        if (draft.colors.length >= 60) break;
        const key = nextTokenKey(draft.colors, "farbe");
        draft.colors.push({
          key,
          name: `Bildfarbe ${key.replace("farbe-", "")}`,
          color,
          cmyk: null,
          pantone: null,
        });
      }
    });
    toast.success(fresh.length === 1 ? "Farbe hinzugefügt" : `${fresh.length} Farben hinzugefügt`, {
      description: "Name und Rolle kannst du in der Farbkarte anpassen.",
    });
  };

  const open = palette?.filter((entry) => !inKit(entry.color)) ?? [];

  return (
    <Card>
      <CardHeader
        title="Farben aus Bild übernehmen"
        description="Ermittelt die prägenden Farben eines Fotos oder Logos. Ein Klick übernimmt eine Farbe in die Marke."
      />
      <div className="space-y-4 p-5">
        <div className="flex flex-wrap gap-2">
          <Button
            size="sm"
            variant="secondary"
            disabled={loading}
            onClick={() => fileInput.current?.click()}
          >
            <Upload /> Bild wählen …
          </Button>
          <Button size="sm" variant="secondary" disabled={loading} onClick={() => setPicking(true)}>
            <ImageIcon /> Aus der Mediathek
          </Button>
          {logo ? (
            <Button
              size="sm"
              variant="secondary"
              disabled={loading}
              onClick={() =>
                void run("Logo", () => paletteFromUrl(logo.asset.thumbnailUrl ?? logo.asset.url))
              }
            >
              <Sparkles /> Vom Logo
            </Button>
          ) : null}
          <input
            ref={fileInput}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(event) => {
              const file = event.target.files?.[0];
              event.target.value = "";
              if (file) void run(file.name, () => paletteFromBlob(file));
            }}
          />
        </div>
        {loading ? <p className="text-sm text-zinc-500">Farben werden ermittelt …</p> : null}
        {palette && palette.length > 0 && !loading ? (
          <div className="space-y-3">
            <p className="text-xs text-zinc-500">Aus „{source}“ – nach Anteil sortiert:</p>
            <div className="flex flex-wrap gap-3">
              {palette.map((entry) => {
                const present = inKit(entry.color);
                return (
                  <button
                    key={entry.color}
                    type="button"
                    disabled={present || tokens.colors.length >= 60}
                    onClick={() => add([entry.color])}
                    title={present ? "Schon in der Marke" : `${entry.color} übernehmen`}
                    className="group flex w-20 flex-col items-center gap-1 text-xs text-zinc-600 disabled:cursor-default"
                  >
                    <span
                      className="relative flex size-14 items-center justify-center rounded-xl ring-1 ring-black/10 ring-inset"
                      style={{ background: entry.color }}
                    >
                      <span className="flex size-6 items-center justify-center rounded-full bg-white/90 text-zinc-800 opacity-0 shadow transition group-hover:opacity-100 group-disabled:opacity-100">
                        {present ? <Check className="size-3.5" /> : <Plus className="size-3.5" />}
                      </span>
                    </span>
                    <span className="font-mono">{entry.color}</span>
                    <span className="text-[11px] text-zinc-400">
                      {Math.round(entry.weight * 100)} %
                    </span>
                  </button>
                );
              })}
            </div>
            {open.length > 1 ? (
              <Button size="sm" onClick={() => add(open.map((entry) => entry.color))}>
                <Plus /> Alle {open.length} übernehmen
              </Button>
            ) : null}
          </div>
        ) : null}
      </div>
      <MediaPickerDialog
        orgId={orgId}
        open={picking}
        onOpenChange={setPicking}
        title="Bild für die Farbpalette"
        onPick={([asset]) => {
          setPicking(false);
          if (asset) void run(asset.name, () => paletteFromUrl(asset.thumbnailUrl ?? asset.url));
        }}
      />
    </Card>
  );
}
